import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { Mermaid } from "mermaid";

import styles from "./Answer.module.css";
import { repairMermaid } from "./repairMermaid";

// Mermaid is a heavy dependency, so we load it lazily (only when a diagram is
// actually rendered) and initialize it once. "strict" security level sanitizes
// labels and disables click handlers / arbitrary HTML inside diagrams.
let mermaidPromise: Promise<Mermaid> | undefined;
export function loadMermaid(): Promise<Mermaid> {
    if (!mermaidPromise) {
        mermaidPromise = import("mermaid").then(mod => {
            const mermaid = mod.default;
            mermaid.initialize({
                startOnLoad: false,
                securityLevel: "strict",
                // Do not inject a "Syntax error in text" SVG into the DOM when a
                // diagram fails to parse. We handle failures ourselves and show the
                // raw definition as a code fallback instead.
                suppressErrorRendering: true,
                theme: "neutral",
                // Use a concrete font stack (matching the app) instead of "inherit".
                // Mermaid measures label text width up front to size the node boxes;
                // with "inherit" the measurement font can differ from the rendered
                // font, so labels get clipped. A concrete stack keeps measurement and
                // rendering consistent.
                fontFamily: 'Roboto, "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif'
            });
            return mermaid;
        });
    }
    return mermaidPromise;
}

interface Props {
    /** The raw Mermaid diagram definition (contents of the ```mermaid fence). */
    chart: string;
}

/**
 * Renders a Mermaid diagram. If the definition is invalid (which can happen for
 * partial content while the answer is still streaming, or if the model emits a
 * malformed diagram), it falls back to showing the raw definition as code so the
 * user still sees something useful instead of an error.
 */
// Zoom limits and step for the lightbox (fit-to-screen is scale 1).
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.2;

export const MermaidDiagram = ({ chart }: Props) => {
    const rawId = useId();
    // useId() can contain ":" which is not a valid character for a mermaid/DOM id.
    const diagramId = `mermaid-${rawId.replace(/[^a-zA-Z0-9-_]/g, "")}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const zoomContainerRef = useRef<HTMLDivElement>(null);
    const [failed, setFailed] = useState(false);
    const [svg, setSvg] = useState<string | null>(null);
    const [zoomOpen, setZoomOpen] = useState(false);

    // Pan/zoom state for the lightbox.
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    // Base pixel width of the zoomed SVG at scale 1. Zooming changes the SVG's
    // actual width (so the browser re-rasterizes the vector crisply) instead of
    // using transform:scale() which blurs on the raster layer.
    const baseWidthRef = useRef<number>(0);
    const dragState = useRef<{ dragging: boolean; startX: number; startY: number; originX: number; originY: number }>({
        dragging: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0
    });

    // Normalize the inline SVG so it scales within its container while preserving
    // aspect ratio (mermaid emits a fixed pixel width/height). We clear the fixed
    // width/height attributes and let the CSS (.mermaidDiagram svg) cap both the
    // width and the height, so large graphs stay compact inline; the zoom lightbox
    // shows them full size.
    const normalizeSvg = useCallback((host: HTMLElement | null) => {
        const svgEl = host?.querySelector("svg") as (SVGElement & { style: CSSStyleDeclaration }) | null;
        if (svgEl) {
            svgEl.removeAttribute("width");
            svgEl.removeAttribute("height");
            svgEl.style.removeProperty("width");
            svgEl.style.removeProperty("height");
            svgEl.style.removeProperty("max-width");
        }
    }, []);

    // Prepare the zoomed SVG: clear mermaid's inline pixel caps and pick a base
    // pixel width that fits the viewport. Zooming then adjusts the SVG's actual
    // width (below), so the browser re-renders the vector crisply at every level.
    const normalizeZoomSvg = useCallback((host: HTMLElement | null) => {
        const svgEl = host?.querySelector("svg") as SVGSVGElement | null;
        if (!svgEl) {
            return;
        }
        svgEl.removeAttribute("width");
        svgEl.removeAttribute("height");
        svgEl.style.removeProperty("max-width");
        svgEl.style.removeProperty("max-height");
        svgEl.style.maxWidth = "none";
        svgEl.style.maxHeight = "none";
        svgEl.style.height = "auto";
        svgEl.style.display = "block";

        // Derive the SVG's intrinsic aspect from its viewBox and choose a base
        // width that fills the screen at scale 1 (fit-to-viewport), so the diagram
        // opens as large as possible while keeping its aspect ratio. A small margin
        // keeps it clear of the window edges / toolbar.
        const vb = svgEl.viewBox?.baseVal;
        const vw = vb && vb.width > 0 ? vb.width : 800;
        const vh = vb && vb.height > 0 ? vb.height : 600;
        const maxW = window.innerWidth * 0.96;
        const maxH = window.innerHeight * 0.92;
        // Largest width that keeps the SVG within both maxW and maxH.
        const base = Math.min(maxW, (vw / vh) * maxH);
        baseWidthRef.current = base;
        svgEl.style.width = `${base}px`;
    }, []);

    useEffect(() => {
        let cancelled = false;
        const trimmed = chart.trim();
        if (!trimmed) {
            return;
        }

        loadMermaid()
            .then(async mermaid => {
                // Validate first: parse() throws on invalid syntax without touching
                // the DOM, so we can fall back cleanly before attempting to render.
                // This also avoids partially-parsed diagrams while streaming.
                let definition = trimmed;
                try {
                    await mermaid.parse(definition);
                } catch {
                    // The model frequently emits invalid node IDs (names with spaces,
                    // trailing digits, or stray [S_xxx] annotations). Try a best-effort
                    // repair before giving up and showing the raw code fallback.
                    const repaired = repairMermaid(definition);
                    await mermaid.parse(repaired);
                    definition = repaired;
                }
                return mermaid.render(diagramId, definition);
            })
            .then(({ svg: rendered }) => {
                if (cancelled) {
                    return;
                }
                setSvg(rendered);
                if (containerRef.current) {
                    containerRef.current.innerHTML = rendered;
                    normalizeSvg(containerRef.current);
                }
                setFailed(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setFailed(true);
                }
                // Remove any stray element mermaid may have appended to the DOM for
                // this diagram id, so a failed render never leaves an artifact behind.
                document.getElementById(diagramId)?.remove();
                document.getElementById(`d${diagramId}`)?.remove();
            });

        return () => {
            cancelled = true;
        };
    }, [chart, diagramId, normalizeSvg]);

    // Inject the SVG into the lightbox when it opens and reset the view.
    useEffect(() => {
        if (zoomOpen && svg && zoomContainerRef.current) {
            zoomContainerRef.current.innerHTML = svg;
            normalizeZoomSvg(zoomContainerRef.current);
        }
    }, [zoomOpen, svg, normalizeZoomSvg]);

    // Apply zoom by changing the SVG's actual width (crisp vector re-render) and
    // pan via translate. Using width instead of transform:scale() avoids the
    // blur that scale() causes by upscaling the already-rasterized layer.
    useEffect(() => {
        if (!zoomOpen || !zoomContainerRef.current) {
            return;
        }
        const svgEl = zoomContainerRef.current.querySelector("svg") as (SVGElement & { style: CSSStyleDeclaration }) | null;
        if (svgEl && baseWidthRef.current > 0) {
            svgEl.style.width = `${baseWidthRef.current * scale}px`;
            svgEl.style.transform = `translate(${offset.x}px, ${offset.y}px)`;
        }
    }, [zoomOpen, svg, scale, offset]);

    // Close the lightbox with the Escape key.
    useEffect(() => {
        if (!zoomOpen) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setZoomOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [zoomOpen]);

    const openZoom = useCallback(() => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
        setZoomOpen(true);
    }, []);

    const clampScale = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

    const zoomIn = useCallback(() => setScale(s => clampScale(s * ZOOM_STEP)), []);
    const zoomOut = useCallback(() => setScale(s => clampScale(s / ZOOM_STEP)), []);
    const resetView = useCallback(() => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
    }, []);

    const onWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
        setScale(s => clampScale(s * factor));
    }, []);

    const onPointerDown = useCallback(
        (e: React.PointerEvent) => {
            dragState.current = {
                dragging: true,
                startX: e.clientX,
                startY: e.clientY,
                originX: offset.x,
                originY: offset.y
            };
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        },
        [offset]
    );

    const onPointerMove = useCallback((e: React.PointerEvent) => {
        if (!dragState.current.dragging) {
            return;
        }
        setOffset({
            x: dragState.current.originX + (e.clientX - dragState.current.startX),
            y: dragState.current.originY + (e.clientY - dragState.current.startY)
        });
    }, []);

    const onPointerUp = useCallback((e: React.PointerEvent) => {
        dragState.current.dragging = false;
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    }, []);

    if (failed) {
        return (
            <pre className={styles.mermaidFallback}>
                <code>{chart}</code>
            </pre>
        );
    }

    return (
        <>
            <div
                ref={containerRef}
                className={styles.mermaidDiagram}
                role="img"
                aria-label="Diagram"
                tabIndex={0}
                title="Click to zoom"
                onClick={openZoom}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openZoom();
                    }
                }}
            />
            {zoomOpen && (
                <div className={styles.mermaidZoomOverlay} role="dialog" aria-modal="true" aria-label="Zoomed diagram" onClick={() => setZoomOpen(false)}>
                    <div className={styles.mermaidZoomToolbar} onClick={e => e.stopPropagation()}>
                        <button type="button" className={styles.mermaidZoomButton} onClick={zoomOut} aria-label="Zoom out" title="Zoom out">
                            −
                        </button>
                        <span className={styles.mermaidZoomLevel}>{Math.round(scale * 100)}%</span>
                        <button type="button" className={styles.mermaidZoomButton} onClick={zoomIn} aria-label="Zoom in" title="Zoom in">
                            +
                        </button>
                        <button type="button" className={styles.mermaidZoomButton} onClick={resetView} aria-label="Reset zoom" title="Reset">
                            ⟲
                        </button>
                        <button type="button" className={styles.mermaidZoomButton} onClick={() => setZoomOpen(false)} aria-label="Close" title="Close">
                            ✕
                        </button>
                    </div>
                    <div
                        className={styles.mermaidZoomStage}
                        onClick={e => e.stopPropagation()}
                        onWheel={onWheel}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        style={{ cursor: dragState.current.dragging ? "grabbing" : "grab" }}
                    >
                        <div ref={zoomContainerRef} className={styles.mermaidZoomContent} />
                    </div>
                </div>
            )}
        </>
    );
};
